import { useReactFlow } from '@xyflow/react';
import { useCallback } from 'react';

/**
 * Hook for handling node minimize/maximize state
 */
export const useNodeMinimize = () => {
  const { getNode, setNodes } = useReactFlow();

  /**
   * Toggle a node's minimized state
   */
  const toggleMinimizeNode = useCallback((nodeId: string) => {
    const node = getNode(nodeId);
    if (!node) return;

    // Get the current state
    const isCurrentlyMinimized = !!node.data?.isMinimized;
    
    if (!isCurrentlyMinimized) {
      // MINIMIZING: Save current dimensions and minimize
      const originalSize = { width: node.width, height: node.height };
      console.log(`[MINIMIZE] Saving size for ${nodeId}:`, originalSize);
      
      // First add the animation class
      setNodes((nodes) => 
        nodes.map((n) => 
          n.id === nodeId 
            ? { 
                ...n, 
                data: { 
                  ...n.data,
                  originalWidth: originalSize.width, 
                  originalHeight: originalSize.height,
                  isMinimizing: true,
                  isMaximizing: false
                }
              } 
            : n
        )
      );
        // After animation starts, update the minimized state
      setTimeout(() => {
        setNodes((nodes) => 
          nodes.map((n) => 
            n.id === nodeId 
              ? { 
                  ...n, 
                  data: { 
                    ...n.data,
                    isMinimized: true,
                    isMinimizing: false
                  },
                  width: 300,
                  height: 50
                } 
              : n
          )
        );
      }, 250); // Match animation duration
    } else {
      // MAXIMIZING: Restore to original dimensions
      // Give priority to explicitly saved dimensions, fall back to reasonable defaults
      const originalWidth = node.data?.originalWidth ?? node.width ?? 900;
      const originalHeight = node.data?.originalHeight ?? node.height ?? 630;
      
      console.log(`[MAXIMIZE] Restoring size for ${nodeId}:`, { width: originalWidth, height: originalHeight });
      
      // First update the node size
      setNodes((nodes) => 
        nodes.map((n) => 
          n.id === nodeId 
            ? { 
                ...n,
                width: originalWidth,
                height: originalHeight,
                data: { 
                  ...n.data,
                  isMaximizing: true,
                  isMinimizing: false
                }
              } 
            : n
        )
      );
      
      // After size is updated, start the animation and unset minimized
      setTimeout(() => {
        setNodes((nodes) => 
          nodes.map((n) => 
            n.id === nodeId 
              ? { 
                  ...n,
                  data: { 
                    ...n.data,
                    isMinimized: false,
                    isMaximizing: false
                  }
                } 
              : n
          )
        );
      }, 10); // Small delay for the size to update
    }
  }, [getNode, setNodes]);

  return { toggleMinimizeNode };
};

